import { useState, useEffect, useCallback, useRef } from "react";
// import { sGet, sPut } from "../../utils/adminApi";
import { sGet, sPut, sPost } from "../../utils/staffApi";
import {
  C,
  Input,
  Select,
  Btn,
  Table,
  Pagination,
  Modal,
  Badge,
} from "./AdminPanel";
import "../../styles/orders.css";

// React Icons imports
import {
  FiSearch,
  FiEye,
  FiEdit2,
  FiPackage,
  FiTruck,
  FiCalendar,
  FiCreditCard,
  FiMapPin,
  FiMail,
  FiUser,
} from "react-icons/fi";
import { FaRupeeSign, FaBoxOpen, FaShippingFast } from "react-icons/fa";
import {
  MdTrackChanges,
  MdPhone,
  MdLocationOn,
  MdPerson,
} from "react-icons/md";

const ORDER_STATUSES = [
  "Processing",
  "Confirmed",
  "Shipped",
  "Out for Delivery",
  "Delivered",
  "Cancelled",
];

export default function Orders({ toast }) {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [updModal, setUpdModal] = useState(null);
  const [updForm, setUpdForm] = useState({
    status: "",
    trackingNumber: "",
    courierPartner: "",
  });
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef();

  const sendInvoice = async (order) => {
    try {
      // Use direct fetch because the endpoint is under /api/orders, not /staff
      const token = localStorage.getItem("kkb_token");
      const res = await fetch(
        `https://lightslategrey-mole-360881.hostingersite.com/api/orders/${order.id}/invoice`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      toast("Invoice sent successfully to customer email!");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    sGet("/orders", { page, search, status })
      .then((d) => {
        setOrders(d.orders);
        setTotal(d.total);
        setPages(d.pages);
      })
      .catch((e) => toast(e.message, "error"))
      .finally(() => setLoading(false));
  }, [page, search, status, toast]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 400);
  }, [load]);

  const openUpdate = (order) => {
    setUpdForm({
      status: order.orderStatus,
      trackingNumber: order.trackingNumber || "",
      courierPartner: order.courierPartner || "",
    });
    setUpdModal(order);
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("kkb_token");

      // Send orderStatus instead of status
      const updatePayload = {
        orderStatus: updForm.status, // ← Changed from 'status' to 'orderStatus'
        trackingNumber: updForm.trackingNumber,
        courierPartner: updForm.courierPartner,
      };

      const res = await fetch(
        `https://lightslategrey-mole-360881.hostingersite.com/api/orders/${updModal.id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify(updatePayload), // ← Use the corrected payload
        },
      );

      const data = await res.json();
      console.log("Update response:", data);

      if (!res.ok) throw new Error(data.message || "Failed to update order");

      toast(
        data.message ||
          "Order updated successfully! WhatsApp notification sent to customer ✓",
      );
      setUpdModal(null);

      if (selected && selected.id === updModal.id) {
        await viewOrder(selected.id);
      }
      await load();
    } catch (e) {
      console.error("Update error:", e);
      toast(e.message || "Failed to update order", "error");
    } finally {
      setSaving(false);
    }
  };

  const viewOrder = async (id) => {
    try {
      const order = await sGet(`/orders/${id}`);
      setSelected(order);
    } catch (e) {
      toast(e.message, "error");
    }
  };

  return (
    <div className="orders-container">
      {/* Toolbar */}
      <div className="orders-toolbar">
        <div className="toolbar-filters">
          <div className="search-input-wrapper">
            <Input
              placeholder="Order ID, email, name…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              icon={<FiSearch />}
            />
          </div>
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Statuses</option>
            {ORDER_STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Select>
        </div>
        <span className="orders-meta">
          <FiPackage /> {total} orders
        </span>
      </div>

      <div className="orders-table-card">
        <Table
          loading={loading}
          columns={[
            {
              key: "orderId",
              label: "Order ID",
              render: (v) => (
                <span className="text-primary text-bold">
                  <FiPackage /> {v}
                </span>
              ),
            },
            {
              key: "user",
              label: "Customer",
              render: (v, row) => (
                <>
                  <FiUser />{" "}
                  {v?.name || row.shippingAddress?.firstName || "Guest"}
                </>
              ),
            },
            {
              key: "shippingAddress",
              label: "Email",
              render: (v) => (
                <span className="text-muted text-small">
                  <FiMail /> {v?.email}
                </span>
              ),
            },
            {
              key: "items",
              label: "Items",
              render: (v) => (
                <>
                  <FaBoxOpen /> {v?.length || 0} item(s)
                </>
              ),
            },
            {
              key: "totalAmount",
              label: "Total",
              render: (v) => (
                <span className="text-gold text-bold">
                  <FaRupeeSign />
                  {v?.toLocaleString()}
                </span>
              ),
            },
            {
              key: "paymentMethod",
              label: "Payment",
              render: (v) => (
                <>
                  <FiCreditCard /> {v?.toUpperCase()}
                </>
              ),
            },
            {
              key: "orderStatus",
              label: "Status",
              render: (v) => <Badge status={v} />,
            },
            {
              key: "createdAt",
              label: "Date",
              render: (v) => (
                <>
                  <FiCalendar /> {new Date(v).toLocaleDateString("en-IN")}
                </>
              ),
            },
            {
              key: "id", // ← changed from "_id"
              label: "Actions",
              render: (v, row) => (
                <div style={{ display: "flex", gap: "5px" }}>
                  <Btn size="sm" variant="ghost" onClick={() => viewOrder(v)}>
                    View
                  </Btn>
                  <Btn size="sm" onClick={() => openUpdate(row)}>
                    Update
                  </Btn>
                  <Btn
                    size="sm"
                    variant="primary"
                    onClick={() => sendInvoice(row)}
                  >
                    📧 Send Invoice
                  </Btn>
                </div>
              ),
            },
          ]}
          data={orders}
          rowKey="id"
        />
      </div>
      <Pagination page={page} pages={pages} onChange={setPage} />

      {/* View Order Modal */}
      {selected && (
        <Modal
          title={`Order #${selected.orderId}`}
          onClose={() => setSelected(null)}
        >
          <div className="order-view-container">
            <div className="order-details-grid">
              {[
                ["Status", <Badge status={selected.orderStatus} />],
                [
                  "Date",
                  <>
                    <FiCalendar />{" "}
                    {new Date(selected.createdAt).toLocaleString("en-IN")}
                  </>,
                ],
                [
                  "Payment",
                  <>
                    <FiCreditCard /> {selected.paymentMethod?.toUpperCase()}
                  </>,
                ],
                [
                  "Total",
                  <>
                    <FaRupeeSign /> ₹{selected.totalAmount?.toLocaleString()}
                  </>,
                ],
              ].map(([l, v]) => (
                <div key={l} className="order-detail-box">
                  <div className="order-detail-label">{l}</div>
                  <div className="order-detail-value">{v}</div>
                </div>
              ))}
            </div>

            <div className="order-address-box">
              <div className="order-detail-label">
                <MdLocationOn /> SHIPPING ADDRESS
              </div>
              <div className="order-address-content">
                <MdPerson /> {selected.shippingAddress?.firstName}{" "}
                {selected.shippingAddress?.lastName}
                <br />
                <MdLocationOn /> {selected.shippingAddress?.address}
                <br />
                <FiMapPin /> {selected.shippingAddress?.city},{" "}
                {selected.shippingAddress?.state} –{" "}
                {selected.shippingAddress?.pincode}
                <br />
                <MdPhone /> {selected.shippingAddress?.phone}
              </div>
            </div>

            <div className="order-items-section">
              <div className="order-detail-label">
                <FaBoxOpen /> Items
              </div>
              {selected.items?.map((item, i) => (
                <div key={i} className="order-item-row">
                  <img
                    src={item.image}
                    alt=""
                    className="order-item-img"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                  <div className="order-item-info">
                    <div className="order-item-name">{item.name}</div>
                    <div className="order-item-meta">
                      Size: {item.size} · Qty: {item.qty}
                    </div>
                  </div>
                  <div className="order-item-price">
                    <FaRupeeSign />
                    {(item.price * item.qty)?.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>

            {selected.trackingNumber && (
              <div className="order-tracking-box">
                <div className="order-detail-label">
                  <MdTrackChanges /> TRACKING
                </div>
                <div className="order-detail-value">
                  <FiTruck /> {selected.trackingNumber}{" "}
                  {selected.courierPartner && `via ${selected.courierPartner}`}
                </div>
              </div>
            )}

            <Btn
              onClick={() => {
                setSelected(null);
                openUpdate(selected);
              }}
              className="btn-full"
            >
              <FiEdit2 /> Update Order Status
            </Btn>
          </div>
        </Modal>
      )}

      {/* Update Status Modal */}
      {updModal && (
        <Modal title="Update Order Status" onClose={() => setUpdModal(null)}>
          <div className="order-update-form">
            <Select
              label="New Status"
              value={updForm.status}
              onChange={(e) =>
                setUpdForm((p) => ({ ...p, status: e.target.value }))
              }
            >
              {ORDER_STATUSES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Select>
            <Input
              label="Tracking Number"
              placeholder="e.g. 1234567890"
              value={updForm.trackingNumber}
              onChange={(e) =>
                setUpdForm((p) => ({ ...p, trackingNumber: e.target.value }))
              }
              icon={<FiTruck />}
            />
            <Input
              label="Courier Partner"
              placeholder="e.g. Delhivery, Bluedart"
              value={updForm.courierPartner}
              onChange={(e) =>
                setUpdForm((p) => ({ ...p, courierPartner: e.target.value }))
              }
              icon={<FaShippingFast />}
            />

            <div className="modal-actions">
              <Btn variant="ghost" onClick={() => setUpdModal(null)}>
                Cancel
              </Btn>
              <Btn onClick={handleUpdate} disabled={saving}>
                {saving ? "Updating & Sending Email…" : "Update Order"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
